import React from 'react';

/*
 * Desktop poker-table responsive scale.
 *
 * PokerTable owns the observed container and calls:
 *   const scale = usePokerTableResponsiveScale(tableRef, !isMobile);
 *   const style = getPokerTableScaleStyle(scale);
 *
 * The hook measures the rendered table container with ResizeObserver and compares it
 * with the desktop design size below. It stores one clamped scale value and exposes it
 * through CSS variables on the table root:
 *   --poker-table-scale: table content such as community cards
 *   --poker-seat-scale: player seats / frames
 *   --poker-ui-scale: floating UI attached to the table
 *   --poker-reaction-scale: throwable/reaction artwork and impact effects
 *
 * Child components should consume those variables in CSS instead of creating their own
 * ResizeObserver. Example for StatusBar or another table overlay:
 *   transform: isMobile ? 'none' : 'scale(var(--poker-ui-scale, 1))';
 *   transformOrigin: 'center top';
 *
 * Example for throwables/reactions:
 *   width: calc(84px * var(--poker-reaction-scale, 1));
 *   height: calc(84px * var(--poker-reaction-scale, 1));
 *
 * Mobile passes enabled=false, so the scale is always 1 and existing mobile layout
 * rules stay independent from this desktop-only resize behavior.
 *
 * POKER_TABLE_RESPONSIVE_SCALE tuning:
 *   desktopBaseWidth: desktop design width used as the 1.0 scale reference.
 *   desktopBaseHeight: desktop design height used as the 1.0 scale reference.
 *   minDesktopScale: smallest allowed desktop scale for tiled/small windows.
 *   maxDesktopScale: largest allowed desktop scale for large windows.
 *   updateThreshold: ignores tiny resize differences to avoid unnecessary rerenders.
 *
 * POKER_TABLE_DESKTOP_SURFACE_LAYOUT tuning:
 *   paddingBlock: vertical space around the table before ResizeObserver measures it.
 *   paddingInline: horizontal space around the table before ResizeObserver measures it.
 *     The inline formula keeps 9-tile short windows compact, but adds side
 *     breathing room for 6-tile windows where the table is width-limited.
 *   marginTop: small gap below the detached-room/header tools.
 *   heightOffset: reserved desktop header/tool height above the table surface.
 */
export const POKER_TABLE_RESPONSIVE_SCALE = {
    desktopBaseWidth: 1024,
    desktopBaseHeight: 1024 / (300 / 127),
    minDesktopScale: 0.58,
    maxDesktopScale: 1.18,
    updateThreshold: 0.01
} as const;

export const POKER_TABLE_DESKTOP_SURFACE_LAYOUT = {
    paddingBlock: 'clamp(4px, calc((100vw - 680px) * 0.12), 60px)',
    paddingInline: 'max(clamp(4px, calc((100vw - 680px) * 0.12), 60px), clamp(4px, calc((100vh - 430px) * 0.28), 28px))',
    marginTop: 'clamp(2px, 0.6vh, 6px)',
    heightOffset: '44px'
} as const;

export type PokerTableScaleStyle = React.CSSProperties & {
    '--poker-table-scale': number;
    '--poker-seat-scale': number;
    '--poker-ui-scale': number;
    '--poker-reaction-scale': number;
};

const clampTableScale = (value: number): number => (
    Math.min(
        POKER_TABLE_RESPONSIVE_SCALE.maxDesktopScale,
        Math.max(POKER_TABLE_RESPONSIVE_SCALE.minDesktopScale, value)
    )
);

const calculateTableScale = (rect: Pick<DOMRectReadOnly, 'width' | 'height'>): number | null => {
    if (!rect.width || !rect.height) return null;

    return clampTableScale(Math.min(
        rect.width / POKER_TABLE_RESPONSIVE_SCALE.desktopBaseWidth,
        rect.height / POKER_TABLE_RESPONSIVE_SCALE.desktopBaseHeight
    ));
};

export const getPokerTableScaleStyle = (scale: number): PokerTableScaleStyle => ({
    '--poker-table-scale': scale,
    '--poker-seat-scale': scale,
    '--poker-ui-scale': scale,
    '--poker-reaction-scale': scale
});

export const usePokerTableResponsiveScale = (
    tableRef: React.RefObject<HTMLElement | null>,
    enabled: boolean
): number => {
    const [tableScale, setTableScale] = React.useState(1);

    React.useLayoutEffect(() => {
        if (!enabled) {
            setTableScale(1);
            return undefined;
        }

        const element = tableRef.current;
        if (!element) return undefined;

        const updateScale = (rect: Pick<DOMRectReadOnly, 'width' | 'height'>) => {
            const nextScale = calculateTableScale(rect);
            if (nextScale === null) return;

            setTableScale((currentScale) => (
                Math.abs(currentScale - nextScale) < POKER_TABLE_RESPONSIVE_SCALE.updateThreshold
                    ? currentScale
                    : nextScale
            ));
        };

        updateScale(element.getBoundingClientRect());

        if (typeof ResizeObserver === 'undefined') {
            const ownerWindow = element.ownerDocument.defaultView || window;
            const handleResize = () => updateScale(element.getBoundingClientRect());
            ownerWindow.addEventListener('resize', handleResize);
            return () => ownerWindow.removeEventListener('resize', handleResize);
        }

        const observer = new ResizeObserver((entries) => {
            const [entry] = entries;
            if (entry) updateScale(entry.contentRect);
        });
        observer.observe(element);

        return () => observer.disconnect();
    }, [enabled, tableRef]);

    return tableScale;
};
